/**
 * Typen und Regeln der Bauteile aus §2 des Designsystems.
 *
 * ## Warum das hier steht und nicht in den `.svelte`-Dateien
 *
 * Zwei Gründe, und der zweite ist der wichtigere:
 *
 * 1. Eine Svelte-Datei exportiert aus ihrem Instanzblock keine Typen —
 *    `import { type X } from './X.svelte'` sieht richtig aus und lässt sich
 *    nicht auflösen. Dieselbe Begründung wie bei `geteilt/typen.ts`.
 * 2. **Prüfbarkeit.** Die Tests dieses Projekts laufen ohne Browser
 *    (`tests/run.mjs`). Was in einer Vorlage steht, ist damit unerreichbar;
 *    was hier steht, ist eine gewöhnliche Funktion. Deshalb liegen die
 *    Entscheidungen der Bauteile hier — welche Panelkopf-Art was trägt, ab
 *    wann ein Ladezustand „dauert länger" sagt, wann der abgetippte Name
 *    passt — und nicht als Bedingung im Markup.
 *
 * Die Bauteile selbst bleiben dumm: nur Darstellung.
 */

import type { Antrieb, Uebernachtung } from '../../lib/reiseart';

/* ══ Panelkopf ═══════════════════════════════════════════════════════════ */

/**
 * Die fünf Arten von Panelkopf.
 *
 * - `ergebnis` — Plan, Laden: die Antwort zuerst, Belege darunter
 * - `suche` — Schlafen, Entdecken, Kajak, Meine Reisen: Titel, Trefferzahl, Suchfläche
 * - `checkliste` — Vorbereitung: Titel und Statuszahl
 * - `schreiben` — Tagebuch, Tagespläne, Reiseeinstellungen: Titel und Speicherstand
 * - `herausgeben` — Teilen, Freigaben, Druck: Titel und ein Satz, was nach außen geht
 */
export type Panelart = 'ergebnis' | 'suche' | 'checkliste' | 'schreiben' | 'herausgeben';

/** Woraus ein Kopf der jeweiligen Art besteht. */
export interface Kopfbau {
  /**
   * `etikett` — Versalien in Mikrogröße, weil bei `ergebnis` der **Antwortsatz**
   * die Überschrift ist und ein zweiter fetter Titel darüber mit ihm
   * konkurrieren würde. Sonst eine gewöhnliche `h2`.
   */
  ueberschrift: 'etikett' | 'titel';
  antwort: boolean;
  belege: boolean;
  /** Der Speicherstand — nur an der Schreibfläche, nie in der Fensterleiste. */
  stand: boolean;
  /** Die Zahl neben dem Titel („7" Treffer). */
  trefferzahl: boolean;
  /** Der Fortschrittsbalken der Checkliste. */
  balken: boolean;
  /** Trennlinie unter dem Kopf — nur beim Herausgeben. */
  trennlinie: boolean;
}

/**
 * Der Bauplan je Art.
 *
 * Diese Tabelle ist der eigentliche Gehalt des Bauteils: Fünf Arten sind
 * **weniger** Arbeit als zehn identische Flächen, aber nur, solange sich nicht
 * zwei Panels derselben Art verschieden aufbauen. Deshalb steht der Aufbau
 * hier als Datum und nicht als Verzweigungskette im Markup.
 *
 * Die Kennzahlenzeile (`belege`) gehört **nur** zu `ergebnis`. Sie an jedes
 * Panel zu setzen war der Grund, warum alle zehn gleich aussahen.
 */
export const PANELKOPF_BAU: Record<Panelart, Kopfbau> = {
  ergebnis: {
    ueberschrift: 'etikett',
    antwort: true,
    belege: true,
    stand: false,
    trefferzahl: false,
    balken: false,
    trennlinie: false
  },
  suche: {
    ueberschrift: 'titel',
    antwort: false,
    belege: false,
    stand: false,
    trefferzahl: true,
    balken: false,
    trennlinie: false
  },
  checkliste: {
    ueberschrift: 'titel',
    antwort: false,
    belege: false,
    stand: false,
    trefferzahl: false,
    balken: true,
    trennlinie: false
  },
  schreiben: {
    ueberschrift: 'titel',
    antwort: false,
    belege: false,
    stand: true,
    trefferzahl: false,
    balken: false,
    trennlinie: false
  },
  herausgeben: {
    ueberschrift: 'titel',
    antwort: false,
    belege: false,
    stand: false,
    trefferzahl: false,
    balken: false,
    trennlinie: true
  }
};

/**
 * Der Bauplan zu einer Art. Unbekanntes fällt auf `suche` zurück — das ist die
 * anspruchsloseste Fassung (Titel und Inhalt), also die, die nichts verspricht,
 * was das Panel nicht liefert.
 */
export function kopfbau(art: string | null | undefined): Kopfbau {
  return PANELKOPF_BAU[art as Panelart] ?? PANELKOPF_BAU.suche;
}

/**
 * Erledigter Anteil einer Checkliste, 0…1.
 *
 * Geklemmt, weil `offen` aus einer anderen Quelle kommen kann als `gesamt`:
 * Ein Balken, der über die Bahn hinausläuft oder nach links zeigt, ist eine
 * sichtbare Lüge; ein bei 0 bzw. 1 stehender ist nur ungenau.
 */
export function erledigtAnteil(offen: number | null, gesamt: number | null): number {
  if (!gesamt || gesamt <= 0) return 0;
  return Math.max(0, Math.min(1, (gesamt - (offen ?? 0)) / gesamt));
}

/**
 * Das Wort neben dem Titel einer Checkliste.
 *
 * „alles erledigt" statt „0 offen": Eine Null ist zu lesen wie eine Zahl, und
 * die Aussage der Zeile ist, dass hier nichts mehr zu tun ist.
 */
export function erledigtWort(offen: number | null): string {
  return offen ? `${offen} offen` : 'alles erledigt';
}

/* ══ Zeilenmuster ════════════════════════════════════════════════════════ */

/**
 * - `tippbar` — öffnet etwas: Chevron, Hover, ganze Zeile ist Trefferfläche
 * - `starr` — reine Anzeige: kein Chevron, kein Hover, kein Zeigefinger
 * - `gewaehlt` — aktuell offen: Akzentrand, Akzentfläche, Chevron bleibt
 * - `haken` — Ablaufpunkt: der Kreis links ist die Andeutung
 */
export type Zeilenfassung = 'tippbar' | 'starr' | 'gewaehlt' | 'haken';

/** Reagiert die Zeile überhaupt auf Berührung? `starr` tut es nie. */
export function istTippbar(fassung: Zeilenfassung, hatKlick: boolean): boolean {
  return fassung !== 'starr' && hatKlick;
}

/**
 * Trägt die Zeile einen Chevron?
 *
 * Er ist die Andeutung „hier öffnet sich etwas" und steht deshalb **nur** an
 * Zeilen, die wirklich etwas öffnen. An einer Haken-Zeile wäre er falsch: Die
 * schaltet um, sie öffnet nichts. An einer Zeile ohne `onclick` wäre er ein
 * Versprechen.
 */
export function zeigtChevron(fassung: Zeilenfassung, hatKlick: boolean): boolean {
  return fassung !== 'starr' && fassung !== 'haken' && hatKlick;
}

/* ══ Leerzustand ═════════════════════════════════════════════════════════ */

/**
 * - `anfang` — noch nichts angelegt: **einladen**, gestrichelter Rahmen
 * - `bedingung` — die Bedingung ist nicht erfüllt: **erklären**, ruhige Fläche
 *
 * Leer wegen eines Fehlers ist **kein** Leerzustand. „Noch nichts notiert."
 * über fünfzehn Einträgen auf dem Server war der schwerste Anzeigefehler des
 * Projekts: Der Satz beschrieb den Inhalt, obwohl der Zustand ein anderer war.
 * Dafür ist `Hinweis art="ernst"` da.
 */
export type Leerart = 'anfang' | 'bedingung';

/**
 * Der gestrichelte Rahmen sagt „hier kommt etwas hin" statt „hier ist etwas
 * kaputt". Bei `bedingung` wäre er falsch — dort soll man nichts anlegen,
 * sondern verstehen.
 */
export function istEinladend(art: Leerart): boolean {
  return art === 'anfang';
}

/* ══ Ladezustand ═════════════════════════════════════════════════════════ */

/**
 * Nach wie vielen Sekunden „Das dauert länger als erwartet." erscheint.
 *
 * Acht Sekunden sind lang genug, dass eine gewöhnlich langsame Antwort die
 * Zeile nicht auslöst, und kurz genug, dass niemand vorher aufgibt.
 */
export const GEDULD_SEKUNDEN = 8;

/**
 * Ist die Geduldsschwelle überschritten?
 *
 * `seit` ist der außerhalb gezählte Stand (iOS reicht ihn durch), `verstrichen`
 * die im Bauteil laufende Uhr. Es genügt, wenn **eine** von beiden über der
 * Schwelle liegt: Wer die Ansicht neu betritt, während die Anfrage schon
 * zwanzig Sekunden läuft, soll den Ausweg sofort sehen und nicht noch einmal
 * acht Sekunden warten.
 *
 * Ein Ladezustand ohne Abbruchbedingung ist schlimmer als ein Fehler — er hält
 * den Nutzer am Warten, statt ihn handeln zu lassen.
 */
export function dauertLaenger(
  verstrichen: boolean,
  seit: number | null,
  geduld: number = GEDULD_SEKUNDEN
): boolean {
  return verstrichen || (seit != null && seit >= geduld);
}

/* ══ Hinweis ═════════════════════════════════════════════════════════════ */

/**
 * Die Stufen für Fehlendes und Gemeldetes.
 *
 * Die **stille** Stufe ist bewusst kein Wert: Sie besteht darin, nichts zu
 * rendern. Wer dafür eine Komponente sucht, hat die Stufe verwechselt — aber
 * die Fläche darf dann auch nicht so tun, als sei die fehlende Angabe „0".
 */
export type Hinweisart = 'ernst' | 'leise' | 'merk' | 'ruhig' | 'erklaerung';

/**
 * Braucht diese Stufe einen Ausweg?
 *
 * Ab „leise" gehört einer dazu. Ein Hinweis ohne ihn ist eine Entschuldigung:
 * Er sagt, dass etwas nicht ging, und lässt den Nutzer damit stehen. Bei
 * `merk` und `ruhig` gibt es nichts zu beheben — „Übernachtung nötig" ist ein
 * Rechenergebnis, „kein Netz" unterwegs der Normalzustand.
 */
export function brauchtAusweg(art: Hinweisart): boolean {
  return art === 'ernst' || art === 'leise';
}

/**
 * Das Symbol der Stufe — oder keines.
 *
 * Nur `ernst` und `ruhig` tragen eines, und beide tragen es aus dem
 * Zeichensatz des Hauses statt als Unicode-Zeichen (siehe `Zeichen.svelte`:
 * „✕" und „·" sind je nach Schrift unterschiedlich schwer). `leise` bekommt
 * keines: Es benennt eine Lücke am Ort der Lücke und soll dort nicht lauter
 * sein als die Zeile daneben.
 */
export function hinweisZeichen(art: Hinweisart): 'warnung' | 'kein-netz' | null {
  if (art === 'ernst') return 'warnung';
  if (art === 'ruhig') return 'kein-netz';
  return null;
}

/* ══ Reiseartmarke ═══════════════════════════════════════════════════════ */

/** Ein Achsenteil der Marke: ein Zeichen, ein Wort, eine Farbe. */
export interface Achsenteil {
  zeichen: 'blitz' | 'auto' | 'mond' | 'karte' | 'halbmond';
  wort: string;
  farbe: string;
}

/**
 * Die Zeichen tragen die Aussage, nicht die Fläche.
 *
 * Vier bunte Marken in einer Reiseliste sähen aus wie vier Warnungen — die
 * Marke ist deshalb neutral hinterlegt (`--art-flaeche`), und die Farbe steckt
 * im Zeichen. Das ist zugleich die Antwort auf die Farbfehlsicht: Zwei Formen
 * bleiben zwei Formen.
 */
export const ANTRIEB_MARKE: Record<Antrieb, Achsenteil> = {
  elektro: { zeichen: 'blitz', wort: 'Elektro', farbe: 'var(--art-elektro)' },
  verbrenner: { zeichen: 'auto', wort: 'Verbrenner', farbe: 'var(--art-verbrenner)' }
};

/**
 * `gemischt` bekommt **kein eigenes Farbtoken**, sondern das der Fahrzeugnacht.
 *
 * `token/anwendung.css` verlangt für jedes Token einen Grund, warum die
 * vorhandene Stufe nicht reicht — und hier reicht sie: Gemischt ist die
 * Fahrzeugwelt plus Unterkünfte, nicht umgekehrt. Vorgabe, Packliste und
 * Nachtvorgabe folgen alle `fahrzeug`; eine eigene Farbe behauptete eine
 * dritte Welt, die es nicht gibt. Unterschieden werden die beiden ohnehin am
 * Zeichen — so steht es oben, und so ist es auch die Antwort auf die
 * Farbfehlsicht.
 */
export const NACHT_MARKE: Record<Uebernachtung, Achsenteil> = {
  fahrzeug: { zeichen: 'mond', wort: 'im Fahrzeug', farbe: 'var(--art-fahrzeug)' },
  gemischt: { zeichen: 'halbmond', wort: 'gemischt', farbe: 'var(--art-fahrzeug)' },
  unterkunft: { zeichen: 'karte', wort: 'Unterkunft', farbe: 'var(--art-unterkunft)' }
};

/**
 * Beide Achsen als ein Satz — `title` und `aria-label` der Marke.
 *
 * Immer **beide**, auch wenn nur eine ungewöhnlich ist: „Verbrenner" allein
 * lässt offen, wo geschlafen wird, und genau daran hängt die halbe Oberfläche.
 */
export function reiseartBeschriftung(antrieb: Antrieb, uebernachtung: Uebernachtung): string {
  return `${ANTRIEB_MARKE[antrieb].wort}, ${NACHT_MARKE[uebernachtung].wort}`;
}

/* ══ Speicherstand ═══════════════════════════════════════════════════════ */

export type Speicherzustand = 'rein' | 'offen' | 'speichert' | 'fehler';

/**
 * Die vier Wörter.
 *
 * „gespeichert" und nicht „Alle Änderungen gespeichert" — der Stand steht
 * neben dem Formular, in das gerade getippt wird, und ein Satz dort liest sich
 * wie eine Aufforderung.
 */
export const SPEICHER_WORT: Record<Speicherzustand, string> = {
  rein: 'gespeichert',
  offen: 'ungespeichert',
  speichert: 'speichert …',
  fehler: 'nicht gespeichert'
};

/**
 * Die Farbe des Punkts.
 *
 * Der Fehlerfall ist der einzige, der Farbe bekommt. „gespeichert" grün zu
 * setzen wäre ein Jubel über eine Selbstverständlichkeit — es bekommt das
 * Grau des Randes. Offen und speichernd tragen den Bernstein der Wartestände:
 * keine Warnung, aber auch nicht fertig.
 */
export function standPunkt(zustand: Speicherzustand): string {
  if (zustand === 'fehler') return 'var(--danger)';
  if (zustand === 'rein') return 'var(--border-stark)';
  return 'var(--merk)';
}

/* ══ Reibung ═════════════════════════════════════════════════════════════ */

/**
 * Passt der abgetippte Name?
 *
 * Wer „Susanne Bauer" tippt, hat gelesen, wen es trifft. Das ist der ganze
 * Zweck — nicht Sicherheit, sondern Aufmerksamkeit. Wer etwas darf, entscheidet
 * der Server bei jeder Anfrage; diese Reibung ist gegen Versehen gedacht, nicht
 * gegen Angreifer.
 *
 * Abweichend von der Vorlage wird **beidseitig** getrimmt: Ein Name, der mit
 * einem Leerzeichen aus der Datenbank kommt, wäre sonst schlicht nicht
 * abtippbar — der Nutzer sieht die Ursache nicht und der Knopf bleibt grau.
 * Groß- und Kleinschreibung bleiben dagegen streng: Abtippen heißt abtippen,
 * und die Zeile daneben zeigt die Vorlage.
 *
 * Ein leerer Sollname ergibt **nie** eine Übereinstimmung. Sonst genügte ein
 * leeres Feld, und aus der Reibung würde ein „Wirklich?"-Dialog.
 */
export function nameStimmt(eingabe: string, wen: string): boolean {
  const soll = wen.trim();
  if (!soll) return false;
  return eingabe.trim() === soll;
}

/* ══ Formularblatt ═══════════════════════════════════════════════════════ */

/**
 * Das Spaltenraster eines Formularabschnitts.
 *
 * `minmax(min(100%, 15rem), 1fr)` und nicht `minmax(15rem, 1fr)`: In der
 * 380 px schmalen Planerleiste ist keine Spalte 15 rem breit, und die harte
 * Untergrenze schöbe das Raster aus der Leiste heraus. `min(100%, …)` lässt
 * die Spalte stattdessen auf die verfügbare Breite schrumpfen.
 */
export function spaltenRaster(spalten: number): string {
  return spalten > 1 ? 'repeat(auto-fit, minmax(min(100%, 15rem), 1fr))' : '1fr';
}
